import React, { useEffect } from 'react';
import {
  ATMHeader,
  ATMTable,
  ATMField,
  ATMForm,
  ATMButton,
} from 'shared-it-appmod-ui';
import { useFieldArray } from 'react-hook-form';
import Lang from 'src/libraries/language';
import {
  INewFireDispatchMethods,
  IAirDrops,
} from 'src/models/fire-fighting-dispatch.model';
import FlightInformationAirDropsCell from './air-drop-cell/air-drops-cell.component';

type IProps = INewFireDispatchMethods;

const initialState: IAirDrops = {
  dipCount: null,
  dropCount: null,
  waterLiters: null,
  waterGallons: null,
};

const getGallonsFromLitters = (liters) => {
  const parse = liters * 0.264172;
  return parse.toFixed(2);
};
const getLittersFromGallons = (gallons) => {
  const parse = gallons * 3.78541;
  return parse.toFixed(2);
};

const parseFixedFloat = (value) => {
  return parseFloat(parseFloat(value).toFixed(2));
};

const getTableValues = (fieldsData) => {
  const data = {
    dipCount: 0,
    dropCount: 0,
    waterLiters: 0,
    waterGallons: 0,
  };
  fieldsData.forEach((fieldData) => {
    data.dipCount += fieldData.dipCount ? parseInt(fieldData.dipCount, 10) : 0;
    data.dropCount += fieldData.dropCount
      ? parseInt(fieldData.dropCount, 10)
      : 0;
    data.waterLiters += fieldData.waterLiters
      ? parseFixedFloat(fieldData.waterLiters)
      : 0;
    data.waterGallons += fieldData.waterGallons
      ? parseFixedFloat(fieldData.waterGallons)
      : 0;
  });
  return data;
};

const FlightInformationAirDropsTable: React.FC<IProps> = ({
  control,
  errors,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'DispatchDrops',
  });

  const handleAdd = () => {
    append(initialState);
  };

  useEffect(() => {
    if (!fields.length) {
      handleAdd();
    }
  }, [fields]);

  const onFieldsChange = control.watchInternal('DispatchDrops');
  const fieldsValues = getTableValues(onFieldsChange);

  useEffect(() => {
    control.setValue('dipCount', fieldsValues.dipCount);
    control.setValue('dropCount', fieldsValues.dropCount);
    control.setValue('waterLiters', fieldsValues.waterLiters.toFixed(2));
    control.setValue('waterGallons', fieldsValues.waterGallons.toFixed(2));
  }, [fieldsValues]);

  const onLitersChange = (value, index) => {
    control.setValue(
      `DispatchDrops[${index}].waterGallons`,
      getGallonsFromLitters(value)
    );
  };

  const onGallonsChange = (value, index) => {
    control.setValue(
      `DispatchDrops[${index}].waterLiters`,
      getLittersFromGallons(value)
    );
  };

  return (
    <>
      <ATMHeader as="h3">{Lang.TTL_REQUEST_FLIGHT_AIR_DROPS}</ATMHeader>
      <ATMTable>
        <ATMTable.Header>
          <ATMTable.Row>
            <ATMTable.HeaderCell>
              {`${Lang.LBL_FIRE_DISPATCH_NUM_OF_DIPS} - ${fieldsValues.dipCount}`}
              <ATMField
                name="dipCount"
                className="hidden"
                as={ATMForm.Input}
                control={control}
                type="hidden"
              />
            </ATMTable.HeaderCell>
            <ATMTable.HeaderCell>
              {`${Lang.LBL_FIRE_DISPATCH_NUM_OF_DROPS} - ${fieldsValues.dropCount}`}
              <ATMField
                name="dropCount"
                className="hidden"
                as={ATMForm.Input}
                control={control}
                type="hidden"
              />
            </ATMTable.HeaderCell>
            <ATMTable.HeaderCell>
              {`${
                Lang.LBL_FIRE_DISPATCH_WATER_DROPPED
              } - ${fieldsValues.waterLiters.toFixed(2)} Liters`}
              <ATMField
                name="waterLiters"
                className="hidden"
                as={ATMForm.Input}
                control={control}
                type="hidden"
              />
            </ATMTable.HeaderCell>
            <ATMTable.HeaderCell colSpan={2}>
              {`${
                Lang.LBL_FIRE_DISPATCH_WATER_DROPPED
              } - ${fieldsValues.waterGallons.toFixed(2)} Gallons`}
              <ATMField
                name="waterGallons"
                className="hidden"
                as={ATMForm.Input}
                control={control}
                type="hidden"
              />
            </ATMTable.HeaderCell>
          </ATMTable.Row>
        </ATMTable.Header>
        <ATMTable.Body>
          {fields.map((item, index) => {
            return (
              <FlightInformationAirDropsCell
                key={item.id}
                control={control}
                errors={errors}
                item={item}
                index={index}
                showRemove={fields.length > 1}
                removeRow={(i) => remove(i)}
                onLitersChange={onLitersChange}
                onGallonsChange={onGallonsChange}
              />
            );
          })}
        </ATMTable.Body>
        <ATMTable.Footer>
          <ATMTable.Row>
            <ATMTable.Cell>
              <ATMButton
                secondary
                type="button"
                color="blue"
                content={Lang.LBL_ADD}
                icon="plus"
                onClick={handleAdd}
              />
            </ATMTable.Cell>
          </ATMTable.Row>
        </ATMTable.Footer>
      </ATMTable>
    </>
  );
};

export default FlightInformationAirDropsTable;
